/**
 * Redrawing after a slicer moves - by hand, or once per Play frame.
 *
 * A single-ship scatter paints its points from the arrays it was built with,
 * so re-slicing it is a pass over those arrays (blank the points now outside,
 * put back the ones now inside) and a canvas repaint: no rebuild, no rescale,
 * the zoom untouched. That is what makes Play smooth on 100k points.
 *
 * Every other chart - lines, multi-ship overlays, grids, comparisons - is
 * rebuilt from the cached responses, with the X zoom carried across. Lines are
 * a few thousand points, so the rebuild is quick enough there too.
 */

import { runtime } from './plot-runtime.ts';
import { rerenderChartFromLast } from './plot-actions.ts';
import { scaleMatches } from './zoom.ts';
import { sliceMask } from './slicers.ts';

/** Rebuild from cache, keeping the X zoom (and every grid panel's) where it was. */
export function redrawKeepingZoom(): void {
  const instances = runtime.gridInstances.length ? runtime.gridInstances : runtime.instance ? [runtime.instance] : [];
  const sc = instances[0]?.scales.x;
  const keep =
    sc && sc.min != null && sc.max != null && runtime.dataRange && !scaleMatches(sc.min, sc.max, runtime.dataRange)
      ? { min: sc.min, max: sc.max }
      : null;
  rerenderChartFromLast();
  if (!keep) return;
  const after = runtime.gridInstances.length ? runtime.gridInstances : runtime.instance ? [runtime.instance] : [];
  for (const u of after) u.setScale('x', keep);
}

/** Apply the current slicer ranges to what is on screen, the cheapest way available. */
export function redrawSlices(): void {
  const live = runtime.liveSlice;
  if (live && runtime.instance && live.instance === runtime.instance) {
    const u = live.instance;
    const keep = sliceMask(live.source);
    live.originals.forEach((orig, k) => {
      const arr = u.data[k + 1] as Array<number | null> | undefined;
      if (!arr) return;
      for (let i = 0; i < orig.length; i++) arr[i] = !keep || keep[i] ? (orig[i] ?? null) : null;
    });
    u.redraw(false, false);
    return;
  }
  redrawKeepingZoom();
}
